/**
 * AutonomyMetrics — the historical, read-only metrics block on the ops surface
 * (ADR-013 §4). First-class time-series charts in the dispatcher's-console
 * aesthetic: the north-star (decisions-per-run → 0) over time, throughput,
 * shipped work, the autonomy mix, and the Trust-Ladder auto-ratify rate —
 * per-project or portfolio, over a selectable window.
 *
 * Strictly awareness, never action: no links, no mutations (VISION — "not a
 * second inbox"). The catalog drives which charts can render; a small
 * presentation map below supplies human labels/colors. Empty/zero ranges
 * degrade to a quiet placeholder rather than a broken axis — the dev DB often
 * has no rollup rows until the daemon's cadence job has run.
 */

import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  mergeSeries,
  rangeBounds,
  ratioSeries,
  rowsAllZero,
  type SeriesPoint,
} from "../lib/metric-series.ts";
import { trpc } from "../lib/trpc.ts";
import { MetricChart } from "./metric-chart.tsx";

// ── API shapes (mirrors metricsRouter; ADR-013 §3) ───────────────────────────

type MetricWindow = "cumulative" | "rolling_24h" | "rolling_7d";

interface CatalogEntry {
  key: string;
  scope: "project" | "portfolio" | "both";
  window: MetricWindow;
}

interface Snapshot {
  date: string | null;
  metrics: Record<string, number>;
  /** The window each metric was computed over — labels the tile ("all-time"/"7d"). */
  windows: Record<string, MetricWindow>;
  derived: { decisions_per_run: number; auto_ratify_rate: number };
  /** The window the derived rate ratios were computed over. */
  derivedWindow: MetricWindow;
}

// Short human label for a metric's snapshot window — rendered on every tile so
// a bare number is never ambiguous (12 runs all-time vs 12 runs in 7 days).
const WINDOW_LABEL: Record<MetricWindow, string> = {
  cumulative: "all-time",
  rolling_24h: "24h",
  rolling_7d: "7d",
};

// ── presentation map (labels only; existence is driven by the catalog) ───────

const PALETTE = {
  accent: "var(--color-accent)",
  working: "var(--color-working)", // teal — the autonomous voice; north-star trend
  green: "var(--color-verdict-greenlit)",
  amber: "var(--color-verdict-parked)",
  red: "var(--color-verdict-trashed)",
  blue: "var(--color-verdict-decompose)",
  neutral: "var(--color-fg-2)",
} as const;

type RangeKey = "7d" | "30d" | "90d";
const RANGE_DAYS: Record<RangeKey, number> = { "7d": 7, "30d": 30, "90d": 90 };

// Every series this block plots. `enabled` is gated on the live catalog so a
// retired metric simply stops charting — no code change.
const SERIES_METRICS = [
  "runs_total",
  "runs_completed",
  "decisions_total",
  "auto_ratified_total",
  "commits",
  "loc_added",
  "loc_removed",
  "projects_collaborative",
  "projects_autonomous",
  // Audit primitive activity (ADR-003/004) — audits fired per day, so the
  // previously-empty `audits` table's usage is visible now that cadence fires it.
  "audits_started",
  "audits_completed",
  "audits_failed",
  // The Watch's out-of-band synthesis output, per day (portfolio-only).
  "watch_observations_created",
  // Merge-failure remediation (feature plan mnx1zt27) — resolution-path + lever
  // breakdown of auto-merge failures, so the "manual git-surgery" slices are
  // visibly shrinking (or not) over time.
  "merge_failures_resolved_auto_dirty_reset",
  "merge_failures_resolved_auto_agent_resolve",
  "merge_failures_resolved_operator_agent_resolve",
  "merge_failures_resolved_manual_tmux",
  "merge_failures_unresolved",
  "merge_failures_lever_on",
  "merge_failures_lever_off",
] as const;
type SeriesMetric = (typeof SERIES_METRICS)[number];

// ── formatters ───────────────────────────────────────────────────────────────

function fmtDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en", { month: "short", day: "numeric", timeZone: "UTC" });
}

function fmtInt(v: number): string {
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(Math.round(v));
}

function fmtRatio(v: number): string {
  return v.toFixed(2);
}

function fmtPct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

// ── small UI atoms (match metrics.tsx / ops.tsx) ─────────────────────────────

function RangePill({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "mono text-[9.5px] uppercase tracking-[0.14em] px-2 py-0.5 rounded transition-colors",
        active
          ? "text-[var(--color-fg-1)] border border-[var(--color-line-bright)]"
          : "text-[var(--color-fg-3)] hover:text-[var(--color-fg-1)]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function StatTile({
  label,
  value,
  hint,
  window,
  emphasis,
  loading,
}: {
  label: string;
  value: string;
  hint?: string;
  /** Short window label (e.g. "all-time", "7d") — states what the number covers. */
  window?: string;
  emphasis?: boolean;
  /** Show a skeleton in place of the value while the snapshot loads. */
  loading?: boolean;
}) {
  return (
    <div className="surface p-3">
      <div className="flex items-start justify-between gap-1.5">
        <div className="mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--color-fg-3)]">
          {label}
        </div>
        {window ? (
          <span className="mono text-[8px] uppercase tracking-[0.12em] text-[var(--color-fg-3)] border border-[var(--color-line)] rounded px-1 py-px leading-none whitespace-nowrap flex-shrink-0">
            {window}
          </span>
        ) : null}
      </div>
      {loading ? (
        <div className={`skel mt-1 rounded ${emphasis ? "h-[26px] w-20" : "h-5 w-16"}`} />
      ) : (
        <div
          className={[
            "mono tabular-nums mt-1 leading-none",
            emphasis
              ? "text-[26px] text-[var(--color-working)]"
              : "text-[20px] text-[var(--color-fg)]",
          ].join(" ")}
        >
          {value}
        </div>
      )}
      {hint ? (
        <div className="mono text-[9.5px] text-[var(--color-fg-3)] mt-1.5">{hint}</div>
      ) : null}
    </div>
  );
}

function ChartCard({
  title,
  legend,
  children,
}: {
  title: string;
  legend?: Array<{ label: string; color: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="surface p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="mono text-[10.5px] uppercase tracking-[0.18em] text-[var(--color-fg-3)]">
          {title}
        </span>
        {legend ? (
          <div className="flex items-center gap-2.5">
            {legend.map((l) => (
              <span key={l.label} className="flex items-center gap-1">
                <span
                  className="w-2 h-2 rounded-sm"
                  style={{ backgroundColor: l.color }}
                  aria-hidden
                />
                <span className="mono text-[9px] text-[var(--color-fg-2)]">{l.label}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

// ── main ─────────────────────────────────────────────────────────────────────

export function AutonomyMetrics() {
  // Scope + range live in the URL so the operator's view survives a refresh and
  // is shareable — same pattern as the runtime metrics route.
  const [params, setParams] = useSearchParams();
  const range = ((): RangeKey => {
    const r = params.get("mrange");
    return r === "7d" || r === "90d" ? r : "30d";
  })();
  const scopeId = params.get("mscope") ?? "*";
  const onRange = (r: RangeKey) => {
    const p = new URLSearchParams(params);
    p.set("mrange", r);
    setParams(p, { replace: true });
  };
  const onScope = (id: string) => {
    const p = new URLSearchParams(params);
    p.set("mscope", id);
    setParams(p, { replace: true });
  };

  const isPortfolio = scopeId === "*";
  const { from, to } = useMemo(() => rangeBounds(RANGE_DAYS[range]), [range]);

  const catalogQ = useQuery({
    queryKey: ["metrics.catalog"],
    queryFn: () => trpc.metrics.catalog.query() as unknown as Promise<CatalogEntry[]>,
    staleTime: 5 * 60_000,
  });

  const projectsQ = useQuery({
    queryKey: ["projects.list"],
    queryFn: () =>
      trpc.projects.list.query() as unknown as Promise<Array<{ id: string; name: string }>>,
    staleTime: 60_000,
  });

  const snapQ = useQuery({
    queryKey: ["metrics.snapshot", scopeId],
    queryFn: () =>
      trpc.metrics.snapshot.query(
        isPortfolio ? undefined : { projectId: scopeId },
      ) as unknown as Promise<Snapshot>,
    refetchInterval: 60_000,
  });

  const catalog = catalogQ.data;
  const scopeOf = useMemo(() => {
    const m = new Map<string, CatalogEntry["scope"]>();
    for (const c of catalog ?? []) m.set(c.key, c.scope);
    return m;
  }, [catalog]);

  // A metric is queryable when the catalog knows it and its scope admits the
  // current scope (portfolio-only metrics don't chart for a single project).
  const queryable = (metric: string): boolean => {
    if (!catalog) return false; // wait for the catalog before firing series
    const s = scopeOf.get(metric);
    if (!s) return false;
    return isPortfolio ? s === "portfolio" || s === "both" : s === "project" || s === "both";
  };

  const seriesResults = useQueries({
    queries: SERIES_METRICS.map((metric) => ({
      queryKey: ["metrics.series", metric, scopeId, from, to],
      queryFn: () =>
        trpc.metrics.series.query({
          metric,
          projectId: isPortfolio ? undefined : scopeId,
          from,
          to,
        }) as unknown as Promise<SeriesPoint[]>,
      enabled: queryable(metric),
      refetchInterval: 60_000,
      staleTime: 30_000,
    })),
  });

  const series = useMemo(() => {
    const out = {} as Record<SeriesMetric, SeriesPoint[] | undefined>;
    SERIES_METRICS.forEach((m, i) => {
      out[m] = seriesResults[i]?.data;
    });
    return out;
  }, [seriesResults]);

  // ── derived chart tables ──
  const dprRows = useMemo(
    () => ratioSeries(series.decisions_total, series.runs_total),
    [series.decisions_total, series.runs_total],
  );
  const ratifyRows = useMemo(
    () => ratioSeries(series.auto_ratified_total, series.decisions_total),
    [series.auto_ratified_total, series.decisions_total],
  );
  const throughputRows = useMemo(
    () => mergeSeries([{ key: "runs_completed", rows: series.runs_completed }]),
    [series.runs_completed],
  );
  const commitRows = useMemo(
    () => mergeSeries([{ key: "commits", rows: series.commits }]),
    [series.commits],
  );
  const locRows = useMemo(
    () =>
      mergeSeries([
        { key: "loc_added", rows: series.loc_added },
        { key: "loc_removed", rows: series.loc_removed },
      ]),
    [series.loc_added, series.loc_removed],
  );
  const mixRows = useMemo(
    () =>
      mergeSeries([
        { key: "projects_collaborative", rows: series.projects_collaborative },
        { key: "projects_autonomous", rows: series.projects_autonomous },
      ]),
    [series.projects_collaborative, series.projects_autonomous],
  );
  const auditRows = useMemo(
    () =>
      mergeSeries([
        { key: "audits_started", rows: series.audits_started },
        { key: "audits_completed", rows: series.audits_completed },
        { key: "audits_failed", rows: series.audits_failed },
      ]),
    [series.audits_started, series.audits_completed, series.audits_failed],
  );
  const watchRows = useMemo(
    () =>
      mergeSeries([{ key: "watch_observations_created", rows: series.watch_observations_created }]),
    [series.watch_observations_created],
  );
  const mergeResolutionRows = useMemo(
    () =>
      mergeSeries([
        { key: "auto_dirty_reset", rows: series.merge_failures_resolved_auto_dirty_reset },
        { key: "auto_agent_resolve", rows: series.merge_failures_resolved_auto_agent_resolve },
        {
          key: "operator_agent_resolve",
          rows: series.merge_failures_resolved_operator_agent_resolve,
        },
        { key: "manual_tmux", rows: series.merge_failures_resolved_manual_tmux },
        { key: "unresolved", rows: series.merge_failures_unresolved },
      ]),
    [
      series.merge_failures_resolved_auto_dirty_reset,
      series.merge_failures_resolved_auto_agent_resolve,
      series.merge_failures_resolved_operator_agent_resolve,
      series.merge_failures_resolved_manual_tmux,
      series.merge_failures_unresolved,
    ],
  );
  const mergeLeverRows = useMemo(
    () =>
      mergeSeries([
        { key: "lever_on", rows: series.merge_failures_lever_on },
        { key: "lever_off", rows: series.merge_failures_lever_off },
      ]),
    [series.merge_failures_lever_on, series.merge_failures_lever_off],
  );

  const snap = snapQ.data;
  const m = snap?.metrics ?? {};
  const projects = projectsQ.data ?? [];
  const scopeName = isPortfolio
    ? "portfolio"
    : (projects.find((p) => p.id === scopeId)?.name ?? "project");

  // Loading affordances (ADR-013): the tiles read from the snapshot; each chart
  // reads from its own series queries. Show a skeleton on the FIRST fetch —
  // `isLoading` is false on background refetches (data already present), so a
  // populated chart never flickers back to a skeleton. Until the catalog lands
  // no series query has fired yet, so treat that window as loading too.
  const tilesLoading = snapQ.isLoading;
  const chartLoading = (...keys: SeriesMetric[]): boolean => {
    if (catalogQ.isLoading) return true;
    return keys.some((k) => {
      const i = SERIES_METRICS.indexOf(k);
      return i >= 0 ? (seriesResults[i]?.isLoading ?? false) : false;
    });
  };

  return (
    <section className="space-y-3">
      {/* Section header + controls */}
      <div className="flex items-center gap-2 px-1 flex-wrap">
        <span className="mono text-[10.5px] uppercase tracking-[0.18em] text-[var(--color-fg-3)]">
          autonomy
        </span>
        <div className="hairline flex-1 min-w-[12px]" />
        <select
          value={scopeId}
          onChange={(e) => onScope(e.target.value)}
          aria-label="metrics scope"
          className="mono text-[10px] bg-[var(--color-bg-2)] border border-[var(--color-line)] rounded px-2 py-0.5 text-[var(--color-fg-1)] max-w-[160px]"
        >
          <option value="*">portfolio</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <div className="flex gap-1">
          {(Object.keys(RANGE_DAYS) as RangeKey[]).map((r) => (
            <RangePill key={r} label={r} active={range === r} onClick={() => onRange(r)} />
          ))}
        </div>
      </div>

      {/* Headline snapshot tiles — live from source. COUNT tiles are cumulative
          (all-time running total); the RATE ratios use a trailing window. Each
          tile states its window so the number is never ambiguous. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatTile
          label="decisions / run"
          value={snap ? fmtRatio(snap.derived.decisions_per_run) : "—"}
          hint="north-star → 0"
          window={snap ? WINDOW_LABEL[snap.derivedWindow] : undefined}
          loading={tilesLoading}
          emphasis
        />
        <StatTile
          label="auto-ratify"
          value={snap ? fmtPct(snap.derived.auto_ratify_rate) : "—"}
          window={snap ? WINDOW_LABEL[snap.derivedWindow] : undefined}
          loading={tilesLoading}
        />
        <StatTile
          label="runs"
          value={fmtInt(m.runs_total ?? 0)}
          hint={scopeName}
          window={snap ? WINDOW_LABEL[snap.windows.runs_total ?? "cumulative"] : undefined}
          loading={tilesLoading}
        />
        <StatTile
          label="commits"
          value={fmtInt(m.commits ?? 0)}
          hint="merged"
          window={snap ? WINDOW_LABEL[snap.windows.commits ?? "cumulative"] : undefined}
          loading={tilesLoading}
        />
      </div>

      {/* North-star: decisions-per-run over time (full width) */}
      <ChartCard title="decisions per run · over time">
        <MetricChart
          data={dprRows}
          series={[{ key: "value", label: "decisions/run", color: PALETTE.working, kind: "line" }]}
          height={180}
          formatX={fmtDay}
          formatY={fmtRatio}
          loading={chartLoading("decisions_total", "runs_total")}
          empty={rowsAllZero(dprRows, ["value"])}
          emptyLabel="no runs in this range yet"
        />
      </ChartCard>

      {/* Two-up grid: throughput, commits, lines changed, auto-ratify */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <ChartCard title="throughput · runs completed / day">
          <MetricChart
            data={throughputRows}
            series={[{ key: "runs_completed", label: "completed", color: PALETTE.green }]}
            formatX={fmtDay}
            formatY={fmtInt}
            loading={chartLoading("runs_completed")}
            empty={rowsAllZero(throughputRows, ["runs_completed"])}
          />
        </ChartCard>

        <ChartCard title="commits / day">
          <MetricChart
            data={commitRows}
            series={[{ key: "commits", label: "commits", color: PALETTE.neutral }]}
            formatX={fmtDay}
            formatY={fmtInt}
            loading={chartLoading("commits")}
            empty={rowsAllZero(commitRows, ["commits"])}
          />
        </ChartCard>

        <ChartCard
          title="lines changed / day"
          legend={[
            { label: "added", color: PALETTE.green },
            { label: "removed", color: PALETTE.red },
          ]}
        >
          <MetricChart
            data={locRows}
            series={[
              { key: "loc_added", label: "added", color: PALETTE.green },
              { key: "loc_removed", label: "removed", color: PALETTE.red },
            ]}
            formatX={fmtDay}
            formatY={fmtInt}
            loading={chartLoading("loc_added", "loc_removed")}
            empty={rowsAllZero(locRows, ["loc_added", "loc_removed"])}
          />
        </ChartCard>

        <ChartCard title="auto-ratify rate · over time">
          <MetricChart
            data={ratifyRows}
            series={[{ key: "value", label: "auto-ratify", color: PALETTE.blue, kind: "line" }]}
            formatX={fmtDay}
            formatY={fmtPct}
            formatValue={fmtPct}
            loading={chartLoading("auto_ratified_total", "decisions_total")}
            empty={rowsAllZero(ratifyRows, ["value"])}
            emptyLabel="no decisions in this range yet"
          />
        </ChartCard>
      </div>

      {/* Autonomy mix — portfolio-only (projects_* are portfolio snapshots) */}
      {isPortfolio ? (
        <ChartCard
          title="autonomy mix · projects"
          legend={[
            { label: "collaborative", color: PALETTE.amber },
            { label: "autonomous", color: PALETTE.green },
          ]}
        >
          <MetricChart
            data={mixRows}
            series={[
              {
                key: "projects_collaborative",
                label: "collaborative",
                color: PALETTE.amber,
                stacked: true,
              },
              {
                key: "projects_autonomous",
                label: "autonomous",
                color: PALETTE.green,
                stacked: true,
              },
            ]}
            formatX={fmtDay}
            formatY={fmtInt}
            loading={chartLoading("projects_collaborative", "projects_autonomous")}
            empty={rowsAllZero(mixRows, ["projects_collaborative", "projects_autonomous"])}
          />
        </ChartCard>
      ) : null}

      {/* Audit primitive activity (ADR-003/004) — audits fired per day. The
          `audits` table read 0 across the whole estate until the cadence job
          wired it to fire on its own; this chart is how "the primitive is alive"
          stays visible (and how a schedule quietly firing parse failures shows
          up as the `failed` series). */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <ChartCard
          title="audit activity · started / completed per day"
          legend={[
            { label: "started", color: PALETTE.working },
            { label: "completed", color: PALETTE.green },
            { label: "failed", color: PALETTE.red },
          ]}
        >
          <MetricChart
            data={auditRows}
            series={[
              { key: "audits_started", label: "started", color: PALETTE.working },
              { key: "audits_completed", label: "completed", color: PALETTE.green },
              { key: "audits_failed", label: "failed", color: PALETTE.red },
            ]}
            formatX={fmtDay}
            formatY={fmtInt}
            loading={chartLoading("audits_started", "audits_completed", "audits_failed")}
            empty={rowsAllZero(auditRows, ["audits_started", "audits_completed", "audits_failed"])}
            emptyLabel="no audits in this range yet"
          />
        </ChartCard>

        {isPortfolio ? (
          <ChartCard title="watch observations · synthesized / day">
            <MetricChart
              data={watchRows}
              series={[
                {
                  key: "watch_observations_created",
                  label: "observations",
                  color: PALETTE.blue,
                },
              ]}
              formatX={fmtDay}
              formatY={fmtInt}
              loading={chartLoading("watch_observations_created")}
              empty={rowsAllZero(watchRows, ["watch_observations_created"])}
              emptyLabel="no observations in this range yet"
            />
          </ChartCard>
        ) : null}
      </div>

      {/* Merge-failure remediation (feature plan mnx1zt27) — how auto-merge
          failures got resolved, and whether the remediation lever was on. The
          headline is the resolution mix: the `manual tmux` + `unresolved` slices
          are the manual-git-surgery cost the feature exists to shrink. */}
      <ChartCard
        title="merge failures · resolution path / day"
        legend={[
          { label: "auto reset", color: PALETTE.working },
          { label: "auto resolve", color: PALETTE.green },
          { label: "operator", color: PALETTE.blue },
          { label: "manual tmux", color: PALETTE.amber },
          { label: "unresolved", color: PALETTE.red },
        ]}
      >
        <MetricChart
          data={mergeResolutionRows}
          series={[
            { key: "auto_dirty_reset", label: "auto reset", color: PALETTE.working, stacked: true },
            {
              key: "auto_agent_resolve",
              label: "auto resolve",
              color: PALETTE.green,
              stacked: true,
            },
            {
              key: "operator_agent_resolve",
              label: "operator",
              color: PALETTE.blue,
              stacked: true,
            },
            { key: "manual_tmux", label: "manual tmux", color: PALETTE.amber, stacked: true },
            { key: "unresolved", label: "unresolved", color: PALETTE.red, stacked: true },
          ]}
          formatX={fmtDay}
          formatY={fmtInt}
          loading={chartLoading(
            "merge_failures_resolved_auto_dirty_reset",
            "merge_failures_resolved_auto_agent_resolve",
            "merge_failures_resolved_operator_agent_resolve",
            "merge_failures_resolved_manual_tmux",
            "merge_failures_unresolved",
          )}
          empty={rowsAllZero(mergeResolutionRows, [
            "auto_dirty_reset",
            "auto_agent_resolve",
            "operator_agent_resolve",
            "manual_tmux",
            "unresolved",
          ])}
          emptyLabel="no merge failures in this range"
        />
      </ChartCard>

      <ChartCard
        title="merge failures · remediation lever / day"
        legend={[
          { label: "lever on", color: PALETTE.working },
          { label: "lever off", color: PALETTE.neutral },
        ]}
      >
        <MetricChart
          data={mergeLeverRows}
          series={[
            { key: "lever_on", label: "lever on", color: PALETTE.working, stacked: true },
            { key: "lever_off", label: "lever off", color: PALETTE.neutral, stacked: true },
          ]}
          formatX={fmtDay}
          formatY={fmtInt}
          loading={chartLoading("merge_failures_lever_on", "merge_failures_lever_off")}
          empty={rowsAllZero(mergeLeverRows, ["lever_on", "lever_off"])}
          emptyLabel="no merge failures in this range"
        />
      </ChartCard>

      <p className="mono text-[9.5px] text-[var(--color-fg-3)] px-1">
        read-only · rolled-up daily · merged-to-main work · {from} → {to}
      </p>
    </section>
  );
}
